import { memo, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiCard,
  MiCheckbox,
  MiContentDrawer,
  MiListCards,
  MiStackedTypography,
} from "@miplatform/components";
import { BUTTON_VARIANTS, DATE_FORMAT } from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";
import { mdiHomeRoof } from "@mdi/js";
import { Box, Divider, Typography } from "@mui/material";
import { OrdersListActionMenu } from "../ActionMenu/OrdersListActionMenu";
import useApprovedOrderActions from "./useApprovedOrderActions";

export const ApprovedOrderActions = memo((props) => {
  const {
    id,
    requestedStartDate,
    priority,
    orderTypeName,
    home,
    modifiedOn,
    onRefresh,
    requester,
  } = props;
  const { classes, theme } = useStyles();
  const {
    showDrawer,
    setShowDrawer,
    isLoading,
    assignments,
    getAssignments,
    releaseAssignments,
  } = useApprovedOrderActions({ id, modifiedOn, onRefresh });

  const {
    address: { addressLine1, city, regionCode, county } = {},
    builderName,
  } = home || {};

  const contentDrawerActions = [
    {
      variant: BUTTON_VARIANTS.TEXT,
      name: "Close",
      onClick: () => setShowDrawer(false),
    },
    {
      variant: BUTTON_VARIANTS.CONTAINED,
      name: "Release Assignments",
      disabled: isLoading,
      onClick: () => releaseAssignments(),
    },
  ];

  const sections = [
    {
      fieldsetStyle: {
        alignItems: "center",
        [theme.breakpoints.down("md")]: {
          flexDirection: "column",
        },
      },
      fieldset: [
        {
          field: "assignment",
          // TODO: MM - Do something with this specific Assignment Id when checked
          renderValue: ({ name }) => {
            return (
              <Box>
                <MiCheckbox
                  label={
                    <Box>
                      <Typography
                        fontWeight="bold"
                        color={theme.palette.primary.main}
                      >
                        {name}
                      </Typography>
                    </Box>
                  }
                  // eslint-disable-next-line react/jsx-boolean-value
                  value={true}
                  color={theme.palette.primary.main}
                  size="medium"
                  onChange={() => {}}
                />
              </Box>
            );
          },
        },
      ],
    },
  ];

  useEffect(() => {
    if (showDrawer) {
      getAssignments(id);
    }
  }, [showDrawer, id]);

  const renderContentDrawerTitle = () => {
    return (
      <Box
        flexDirection="row"
        display="flex"
        gap={theme.spacing(2)}
        alignItems="center"
      >
        <Typography variant="h5" fontWeight="bold">
          {addressLine1}
        </Typography>
        <Divider orientation="vertical" flexItem />
        <Typography style={{ color: theme.palette.grey.main }}>
          {`${city}, ${regionCode}`}
        </Typography>
        <Divider orientation="vertical" flexItem />
        <Typography style={{ color: theme.palette.grey.main }}>
          {`${county}`}
        </Typography>
        <Divider orientation="vertical" flexItem />
        <Typography style={{ color: theme.palette.grey.main }}>
          {`${builderName}`}
        </Typography>
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <MiButton
        title="Generate Assignments"
        variant={BUTTON_VARIANTS.CONTAINED}
        onClick={() => setShowDrawer(true)}
      />
      <MiContentDrawer
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        title={renderContentDrawerTitle()}
        variant="temporary"
        actions={contentDrawerActions}
        leftIcon={mdiHomeRoof}
        isLoading={isLoading}
      >
        <Box className={classes.cardBoxContainer}>
          <Typography variant="h4" fontWeight="bold">
            {orderTypeName} Order
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={4}>
            <MiStackedTypography
              title="Requester"
              className={classes.fieldMargin}
              description={
                requester?.firstName
                  ? `${requester?.firstName} ${requester?.lastName}`
                  : ""
              }
            />
            <Divider orientation="vertical" flexItem />
            <MiStackedTypography
              title="Bathrooms"
              className={classes.fieldMargin}
              description={home?.complexity || ""}
            />
            <MiStackedTypography
              title="Power"
              className={classes.fieldMargin}
              description={home?.waterHeaterType || ""}
            />
            <MiStackedTypography
              title="Priority"
              className={classes.fieldMargin}
              description={priority || ""}
            />
            <Divider orientation="vertical" flexItem />
            <MiStackedTypography
              title="Requested Date"
              className={classes.fieldMargin}
              description={formatDate(requestedStartDate, DATE_FORMAT)}
            />
          </Box>

          <MiCard
            title="Assignments"
            containerStyles={{ width: "100%", padding: 10, marginTop: 10 }}
          >
            {assignments?.length > 0 && (
              <MiListCards
                cardProps={{
                  sections,
                  containerStyles: {
                    border: "2px solid",
                    borderRadius: 5,
                    borderColor: theme.palette.primary.main,
                    padding: 5,
                  },
                }}
                data={assignments}
                loading={isLoading}
              />
            )}
          </MiCard>
        </Box>
      </MiContentDrawer>
      <OrdersListActionMenu
        menuViewConfig={{
          Reschedule: true,
          Hold: true,
          Cancel: true,
          OrderActivityDetails: true,
          ForceComplete: true,
          Push24h: false,
        }}
        {...props}
      />
    </Box>
  );
});

const useStyles = makeStyles()(() => ({
  container: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  cardBoxContainer: {
    width: "100%",
    paddingLeft: 20,
    paddingRight: 20,
  },
  miCardStyles: {
    width: "100%",
    padding: 10,
    marginTop: 10,
  },
  selectBox: {
    width: 200,
    marginTop: 5,
  },
}));
